import type { FlashListRef } from '@shopify/flash-list'
import { useGlobalSearchParams, useNavigation } from 'expo-router'
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  useWindowDimensions,
  View,
  ViewToken,
} from 'react-native'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import AnimatedResetFiltersBanner from '@/components/fosters/AnimatedResetFiltersBanner'
import FosterGridItem from '@/components/fosters/FosterGridItem'
import NewFoster from '@/components/fosters/NewFoster'
import FosterGridHeaderContent from '@/components/navigation/header/FosterGridHeaderContent'
import BillingMessage from '@/components/shared/BillingMessage'
import TourSpot from '@/components/tour/TourSpot'
import UniAnimatedFAB from '@/components/unistyles/UniAnimatedFAB'
import UniFlashList from '@/components/unistyles/UniFlashList'
import UniIcon from '@/components/unistyles/UniIcon'
import UniRefreshControl from '@/components/unistyles/UniRefreshControl'
import UniText from '@/components/unistyles/UniText'
import FosterService from '@/services/fosterService'
import { useAppStore } from '@/store/useAppStore'
import * as datadog from '@/utils/datadog/datadog'
import trpc from '@/utils/trpc'

const headerRight = () => <FosterGridHeaderContent />

const MAX_COLUMNS = 7

const EmptyListComponent = memo(
  ({
    basicFilters,
    advancedFilters,
    theme,
  }: {
    basicFilters: { isActive: boolean; isDirty: boolean }
    advancedFilters: { isActive: boolean; isDirty: boolean }
    theme: any
  }) => {
    const hasActiveFilters =
      (basicFilters.isActive && basicFilters.isDirty) ||
      (advancedFilters.isActive && advancedFilters.isDirty)

    return (
      <View style={styles.emptyContainer}>
        <View style={styles.emptyIconContainer}>
          <UniIcon
            color={theme.colors.primary}
            size={48}
            source={hasActiveFilters ? 'filter-remove' : 'paw'}
          />
        </View>
        <UniText style={styles.emptyText}>
          {hasActiveFilters ? 'No matches found' : 'Start Fostering'}
        </UniText>
        {!hasActiveFilters && (
          <UniText style={styles.emptySubtext}>
            Keep track of your fosters' health care, adoption info, and
            documents all in one place. Tap the button below to add your first
            foster.
          </UniText>
        )}
        {hasActiveFilters && (
          <UniText style={styles.emptySubtext}>
            Try broadening your search criteria or removing some filters to see
            more fosters
          </UniText>
        )}
      </View>
    )
  }
)

EmptyListComponent.displayName = 'EmptyListComponent'

const Grid = () => {
  const navigation = useNavigation()
  const { theme, rt } = useUnistyles()
  const [isRefreshing, setRefreshing] = useState(false)
  const [isFABExtended, setIsFABExtended] = useState(true)
  const [fabSize, setFabSize] = useState({ height: 56, width: 56 })
  const [isNewFosterSheetVisible, setIsNewFosterSheetVisible] = useState(false)
  const utils = trpc.useUtils()
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  const { id: selectedFosterId } = useGlobalSearchParams<{ id?: string }>()

  // Track the actual container width so the grid adapts when embedded in a split view
  const [containerWidth, setContainerWidth] = useState(0)

  // We have to use rt.screen.width for iOS because useWindowDimensions is not always accurate when the window is resized. All other platforms use useWindowDimensions.
  const iosWidth = rt.screen.width
  const { width } = useWindowDimensions()
  const fallbackWidth = Platform.OS === 'ios' ? iosWidth : width
  const actualWidth = containerWidth || fallbackWidth

  const currentOrganization = useAppStore.use.currentOrganization().organization

  const bulkSelect = useAppStore.use.bulkSelect()
  const setBulkSelect = useAppStore.use.setBulkSelect()
  const basicFilters = useAppStore.use.basicFilters()
  const advancedFilters = useAppStore.use.advancedFilters()
  const setBasicFilters = useAppStore.use.setBasicFilters()
  const setAdvancedFilters = useAppStore.use.setAdvancedFilters()
  const resetBasicFilters = useAppStore.use.resetBasicFilters()
  const resetAdvancedFilters = useAppStore.use.resetAdvancedFilters()

  const {
    fetchNextPage,
    fosterList,
    refreshFosterList,
    totalFosters,
    isLoading,
  } = FosterService.useFosterList()

  const prefetchFosterDetails = useCallback(
    (fosterId: string) => {
      if (currentOrganization?.id) {
        utils.foster.byId.prefetch(
          {
            id: fosterId,
            organizationId: currentOrganization.id,
          },
          {
            // By default react-query will keep a prefetch request around for 5 minutes before discarding it,
            // so we want to make sure we only prefetch once during this time, and we're ok with stale data. The idea is to make the
            // detail loading experience feel faster, and if tanstack fetches fresher data after that view loads we don't really care
            staleTime: 300 * 1000,
          }
        )
      }
    },
    [currentOrganization?.id, utils.foster.byId]
  )

  type FosterRow = (typeof fosterList)[number]
  type FosterViewToken = ViewToken<FosterRow>

  const listRef = useRef<FlashListRef<FosterRow>>(null)

  const debouncedPrefetch = useCallback(
    (viewableItems: FosterViewToken[]) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      debounceTimerRef.current = setTimeout(() => {
        viewableItems.forEach((item) => {
          if (item?.item?.id) {
            prefetchFosterDetails(item.item.id)
          }
        })
      }, 50)
    },
    [prefetchFosterDetails]
  )

  useEffect(() => {
    const titleSuffix = totalFosters > 0 ? ` (${totalFosters})` : ''
    navigation.getParent()?.setOptions({
      headerTitle: basicFilters.filters.viewAll
        ? `All Fosters${titleSuffix}`
        : `My Fosters${titleSuffix}`,
      headerRight,
    })
  }, [navigation, basicFilters.filters.viewAll, totalFosters])

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    }
  }, [])

  const onRefresh = useCallback(async () => {
    setRefreshing(true)
    await refreshFosterList()
    setRefreshing(false)
  }, [refreshFosterList])

  const onScroll = useCallback(
    ({ nativeEvent }: NativeSyntheticEvent<NativeScrollEvent>) => {
      const currentScrollPosition =
        Math.floor(nativeEvent?.contentOffset?.y) ?? 0
      setIsFABExtended(currentScrollPosition <= 500)
    },
    []
  )

  const onSelect = useCallback(
    (id: string) => {
      if (bulkSelect.selection.items.includes(id)) {
        setBulkSelect({
          selection: {
            items: bulkSelect.selection.items.filter((i) => i !== id),
          },
        })
      } else {
        const newSelected = [...bulkSelect.selection.items, id]
        setBulkSelect({
          selection: {
            items: newSelected,
          },
        })
      }
    },
    [bulkSelect.selection.items, setBulkSelect]
  )

  const itemSize = actualWidth < 400 ? 145 : 200
  const numColumns = Math.min(MAX_COLUMNS, Math.floor(actualWidth / itemSize))

  // Keep the selected foster centered when the grid column count changes or a
  // new foster is opened in the detail panel.
  const scrollTargetRef = useRef<string | null>(null)
  const prevSelectedFosterIdRef = useRef<string | undefined>(selectedFosterId)

  useLayoutEffect(() => {
    const fosterChanged = selectedFosterId !== prevSelectedFosterIdRef.current
    prevSelectedFosterIdRef.current = selectedFosterId

    if (!selectedFosterId) {
      scrollTargetRef.current = null
      return
    }

    const scrollKey = `${selectedFosterId}-${numColumns}`
    if (scrollKey === scrollTargetRef.current) return
    scrollTargetRef.current = scrollKey

    const index = fosterList.findIndex((f) => f.id === selectedFosterId)
    if (index < 0) return

    const timer = setTimeout(() => {
      try {
        listRef.current?.scrollToIndex({
          index,
          animated: fosterChanged,
          viewPosition: 0.5,
        })
      } catch {
        // FlashList may throw if the index hasn't been laid out yet
      }
    }, 0.5)

    return () => clearTimeout(timer)
  }, [numColumns, selectedFosterId, fosterList])

  const renderItem = useCallback(
    ({ item }: { item: (typeof fosterList)[number] }) => (
      <View style={styles.gridItem}>
        <FosterGridItem
          foster={item}
          isSelected={bulkSelect.selection.items.includes(item.id)}
          onSelect={onSelect}
        />
      </View>
    ),
    [bulkSelect.selection.items, onSelect]
  )

  const shouldShowEmptyState = fosterList.length === 0 && !isLoading

  const resetFilters = useCallback(() => {
    const wasAdvancedActive = advancedFilters.isActive
    resetBasicFilters()
    resetAdvancedFilters()
    if (wasAdvancedActive) {
      const state = useAppStore.getState()
      setBasicFilters({ ...state.basicFilters, isActive: true })
      setAdvancedFilters({ ...state.advancedFilters, isActive: false })
    }
  }, [
    advancedFilters.isActive,
    resetBasicFilters,
    resetAdvancedFilters,
    setBasicFilters,
    setAdvancedFilters,
  ])

  return (
    <View
      onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
      style={styles.root}
    >
      <BillingMessage />
      <AnimatedResetFiltersBanner
        advancedFilters={advancedFilters}
        basicFilters={basicFilters}
        onReset={resetFilters}
      />
      {shouldShowEmptyState ? (
        <EmptyListComponent
          advancedFilters={advancedFilters}
          basicFilters={basicFilters}
          theme={theme}
        />
      ) : (
        <UniFlashList
          contentContainerStyle={styles.gridContent}
          data={fosterList}
          extraData={bulkSelect.selection}
          keyExtractor={(foster) => foster.id}
          maintainVisibleContentPosition={{
            disabled: false,
          }}
          numColumns={numColumns}
          onEndReached={() => fetchNextPage()}
          onEndReachedThreshold={0.5}
          onScroll={onScroll}
          onScrollBeginDrag={() => {
            datadog.startAction({
              type: datadog.RumActionType.SCROLL,
              name: 'fosterGrid',
            })
          }}
          onScrollEndDrag={() => {
            datadog.stopAction({
              type: datadog.RumActionType.SCROLL,
              name: 'fosterGrid',
            })
          }}
          onViewableItemsChanged={({ viewableItems }) => {
            debouncedPrefetch(viewableItems)
          }}
          ref={listRef}
          refreshControl={
            <UniRefreshControl
              colors={[theme.colors.primary]}
              onRefresh={onRefresh}
              refreshing={isRefreshing}
            />
          }
          renderItem={renderItem}
          testID="foster-grid"
          viewabilityConfig={{
            itemVisiblePercentThreshold: 80,
            minimumViewTime: 150,
          }}
        />
      )}
      <UniAnimatedFAB
        animateFrom="right"
        disabled={!currentOrganization?.subscriptionActive}
        extended={isFABExtended}
        icon="plus"
        iconMode="dynamic"
        label={Platform.OS === 'ios' ? 'Add a Foster ' : 'Add a Foster'}
        onLayout={(e) =>
          setFabSize({
            height: e.nativeEvent.layout.height,
            width: e.nativeEvent.layout.width,
          })
        }
        onPress={() => setIsNewFosterSheetVisible(true)}
        style={styles.fabStyle}
      />
      <TourSpot
        id="new-foster-button"
        pointerEvents="none"
        style={[
          styles.fabMarker,
          { height: fabSize.height, width: fabSize.width },
        ]}
      />
      <NewFoster
        onClose={() => setIsNewFosterSheetVisible(false)}
        open={isNewFosterSheetVisible}
      />
    </View>
  )
}

const styles = StyleSheet.create((theme) => ({
  emptyContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    padding: theme.tokens.spacing[4],
    paddingBottom: 100,
  },
  emptyIconContainer: {
    alignItems: 'center',
    backgroundColor: theme.colors.secondaryContainer,
    borderRadius: 60,
    height: 120,
    justifyContent: 'center',
    marginBottom: theme.tokens.spacing[6],
    width: 120,
  },
  emptySubtext: {
    color: theme.colors.subtitle,
    fontSize: 16,
    marginTop: theme.tokens.spacing[2],
    maxWidth: 300,
    textAlign: 'center',
  },
  emptyText: {
    color: theme.colors.onSurface,
    fontSize: 20,
    fontWeight: '600',
    maxWidth: 300,
    textAlign: 'center',
  },
  fabMarker: {
    bottom: 32,
    position: 'absolute',
    right: 24,
  },
  fabStyle: {
    borderRadius: 500,
    bottom: 32,
    position: 'absolute',
    right: 24,
  },
  gridContent: {
    flexGrow: 1,
    padding: theme.tokens.spacing[3],
  },
  gridItem: {
    margin: theme.tokens.spacing[2],
  },
  root: {
    backgroundColor: theme.colors.background,
    flex: 1,
  },
}))

export default Grid
